import type { JobResultResponse } from '../../../../../../packing/services/packing-service';
import { getOrderItemAssignmentKey, parseOrderItemKeyFromPackingItemId } from '../../../../../../packing/utils/data-transformer';
import type { AssignedItem, OrderItem, Package } from '../../../../../../components/modals/multi-package-modal-dnd/types';

type PackageWithAssignments = Package & {
  assignedItems: AssignedItem[];
};

type ItemPositions = NonNullable<JobResultResponse['itemPositions']>;

const countAssignments = (
  positions: ItemPositions | undefined,
  orderItemKeys: Set<string>,
  assignedUnitIds: Set<string>,
): AssignedItem[] => {
  const quantityByOrderItemKey = new Map<string, number>();
  positions?.forEach((position) => {
    const unitId = position.item?.id;
    const orderItemKey = parseOrderItemKeyFromPackingItemId(unitId);
    if (!unitId || !orderItemKey || !orderItemKeys.has(orderItemKey) || assignedUnitIds.has(unitId)) return;

    assignedUnitIds.add(unitId);
    quantityByOrderItemKey.set(orderItemKey, (quantityByOrderItemKey.get(orderItemKey) ?? 0) + 1);
  });
  return Array.from(quantityByOrderItemKey, ([orderItemId, quantity]) => ({ orderItemId, quantity }));
};

export const populatePackagesWithAssignments = (
  packages: Package[],
  result: JobResultResponse | null,
  orderItems: OrderItem[],
): PackageWithAssignments[] => {
  if (!result || packages.length === 0) {
    return packages.map((pkg) => ({ ...pkg, assignedItems: [] }));
  }

  const orderItemKeys = new Set<string>();
  orderItems.forEach((item) => {
    const orderItemKey = getOrderItemAssignmentKey(item);
    if (orderItemKey) orderItemKeys.add(String(orderItemKey));
  });
  const assignedUnitIds = new Set<string>();

  if (result.isMultiBox && result.boxes && result.boxes.length > 0) {
    return packages.map((pkg, packageIndex) => ({
      ...pkg,
      assignedItems: countAssignments(result.boxes?.[packageIndex]?.itemPositions, orderItemKeys, assignedUnitIds),
    }));
  }

  if (result.optimalBox && result.itemPositions && result.itemPositions.length > 0) {
    return [
      { ...packages[0], assignedItems: countAssignments(result.itemPositions, orderItemKeys, assignedUnitIds) },
      ...packages.slice(1).map((pkg) => ({ ...pkg, assignedItems: [] })),
    ];
  }

  return packages.map((pkg) => ({ ...pkg, assignedItems: [] }));
};
